<template>
  <b-card
    title="Ajouter un livreur"
    aria-label="Formulaire d'ajout d'un livreur"
  >
    <span class="sr-only">Remplissez ce formulaire pour ajouter un nouveau livreur à la flotte logistique. Tous les champs marqués obligatoires doivent être renseignés.</span>
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
    >
      <validation-observer
        ref="formValidator"
        #default="{ handleSubmit }"
      >
        <b-form
          aria-label="Formulaire d'ajout de livreur"
          @submit.prevent="handleSubmit(submitForm)"
        >
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations personnelles
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="username"
                  aria-label="Nom du livreur"
                  description="Saisissez le nom complet du livreur."
                >
                  <template #label>
                    Nom du livreur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="username"
                        v-model="formData.username"
                        :state="errors.length > 0 ? false : (valid && formData.username ? true : null)"
                        placeholder="Entrez le nom complet"
                        aria-label="Entrée du nom du livreur"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <!-- Email -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="email"
                  aria-label="Adresse email du livreur"
                  description="Saisissez l'adresse email professionnelle du livreur."
                >
                  <template #label>
                    Email <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Email"
                    rules="required|email|asyncEmail"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="email"
                        v-model="formData.email"
                        type="email"
                        :state="errors.length > 0 ? false : (valid && formData.email ? true : null)"
                        placeholder="Entrez l'email"
                        aria-label="Entrée de l'adresse email du livreur"
                        class="premium-input"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="RefreshCwIcon"
                          :class="formData.username && formData.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          v-b-tooltip.hover="emailGenerationTooltip"
                          @click="formData.username && formData.username.trim().length > 0 ? generateCompanyEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="phone"
                  aria-label="Numéro de téléphone du livreur"
                  description="Saisissez le numéro de téléphone professionnel du livreur."
                >
                  <template #label>
                    téléphone professionnel <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Téléphone"
                    rules="required|tnPhone|asyncPhone"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="formData.phone"
                        :formatter="formatPhone"
                        :state="errors.length > 0 ? false : (valid && formData.phone ? true : null)"
                        placeholder="Entrez le numéro de téléphone"
                        maxlength="8"
                        aria-label="Entrée du numéro de téléphone du livreur"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone 2 -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="téléphone personnel"
                  label-for="phone2"
                  aria-label="Numéro de téléphone personnel du livreur"
                  description="Saisissez le numéro de téléphone personnel du livreur."
                >
                  <b-form-input class="premium-input"
                    id="phone2"
                    v-model="formData.phone2"
                    :formatter="formatPhone"
                    placeholder="Entrez le numéro de téléphone personnel"
                    maxlength="8"
                    aria-label="Entrée du numéro de téléphone personnel du livreur"
                  />
                </b-form-group>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Adresse"
                  label-for="address"
                  aria-label="Adresse du livreur"
                  description="Saisissez l'adresse du livreur (optionnel)."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Adresse"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-textarea
                        id="address"
                        v-model="formData.address"
                        :state="errors.length > 0 ? false : (valid && formData.address ? true : null)"
                        placeholder="Entrez l'adresse"
                        aria-label="Entrée de l'adresse du livreur"
                        rows="3"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.address"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon textarea-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Repository -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="repository"
                  aria-label="Dépôt logistique du livreur"
                  description="Sélectionnez le dépôt auquel le livreur est rattaché."
                >
                  <template #label>
                    Dépôt <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Dépôt"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <v-select
                        id="repository"
                        v-model="formData.repository"
                        :options="repositoryOptions"
                        label="name"
                        :reduce="repo => repo.id"
                        placeholder="Sélectionnez un dépôt"
                        :state="errors.length > 0 ? false : (valid && formData.repository ? true : null)"
                        :clearable="false"
                        aria-label="Liste des dépôts logistiques disponibles"
                        class="premium-select"
                        :class="{'is-valid': valid && formData.repository, 'is-invalid': errors.length > 0}"
                      />
                      <feather-icon
                        v-if="valid && formData.repository"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon select-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Owns Vehicle -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Propriétaire du Véhicule"
                  label-for="owns_vehicle"
                  description="Le livreur possède-t-il son propre véhicule ?"
                >
                  <b-form-checkbox
                    id="owns_vehicle"
                    v-model="formData.owns_vehicle"
                    switch
                  >
                    {{ formData.owns_vehicle ? 'Oui' : 'Non' }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Véhicule"
                  label-for="car"
                  aria-label="Véhicule attribué au livreur"
                  description="Sélectionnez le véhicule attribué au livreur (optionnel)."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Véhicule"
                  >
                    <div class="d-flex align-items-center">
                      <v-select
                        id="car"
                        v-model="formData.car"
                        :options="carOptions"
                        label="displayName"
                        :reduce="car => car.id"
                        placeholder="Sélectionnez un véhicule (optionnel)"
                        :state="errors.length > 0 ? false : null"
                        clearable
                        class="flex-grow-1 mr-1"
                        aria-label="Liste des véhicules disponibles"
                      />
                      <b-button
                        variant="outline-primary"
                        class="btn-icon"
                        v-b-tooltip.hover="'Créer un véhicule rapidement'"
                        @click="$refs.quickCreateCarModal.show()"
                      >
                        <feather-icon icon="ZapIcon" />
                      </b-button>
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <h5 class="mt-3 mb-3">
              <feather-icon
                icon="DollarSignIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations de Paiement
            </h5>

            <b-row>
              <!-- Payment Type -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Type de Paiement"
                  label-for="pay_type"
                  description="Sélectionnez le mode de rémunération du livreur."
                >
                  <v-select class="premium-input"
                    id="pay_type"
                    v-model="formData.pay_type"
                    :options="paymentTypeOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Sélectionnez un type de paiement (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>

              <!-- Compensation Amount -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  :label="compensationLabel"
                  label-for="compensation"
                  :description="compensationDescription"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Montant"
                    rules="priceDecimal"
                  >
                    <b-form-input class="premium-input"
                      id="compensation"
                      v-model="formData.compensation"
                      :formatter="formatPrice"
                      type="text"
                      inputmode="decimal"
                      placeholder="0.000"
                      :state="errors.length > 0 ? false : null"
                      @keypress="allowOnlyDecimalNumbers"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <h5 class="mt-3 mb-3">
              <feather-icon
                icon="LockIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Informations d'accès
            </h5>

            <b-row>
              <!-- Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="password"
                  aria-label="Mot de passe du livreur"
                  description="Définissez un mot de passe sécurisé (au moins 8 caractères)."
                >
                  <template #label>
                    Mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input class="premium-input"
                        id="password"
                        v-model="formData.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : (passwordStrength > 25 ? true : null)"
                        placeholder="Entrez le mot de passe"
                        aria-label="Entrée du mot de passe du livreur"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-label="Afficher ou masquer le mot de passe"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <!-- Password Strength Indicator -->
                    <div
                      v-if="formData.password"
                      class="password-strength-wrapper mt-2"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <small class="strength-label">Force du mot de passe :</small>
                        <small
                          class="strength-text"
                          :class="passwordStrengthClass"
                        >{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="6px"
                        class="premium-progress"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="confirmPassword"
                  aria-label="Confirmation du mot de passe du livreur"
                  description="Ressaisissez le mot de passe pour confirmation."
                >
                  <template #label>
                    Confirmer le mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input class="premium-input"
                        id="confirmPassword"
                        v-model="formData.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="getConfirmPasswordState(errors)"
                        placeholder="Confirmez le mot de passe"
                        aria-label="Entrée de confirmation du mot de passe du livreur"
                        @input="onPasswordInput"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-label="Afficher ou masquer la confirmation du mot de passe"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>

                    <!-- Real-time validation feedback -->
                    <div
                      v-if="formData.confirm_password"
                      class="mt-1"
                    >
                      <small
                        v-if="passwordsMatch && formData.password"
                        class="text-success d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="CheckCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe correspondent
                      </small>
                      <small
                        v-else-if="formData.password"
                        class="text-danger d-flex align-items-center validation-message-animate"
                      >
                        <feather-icon
                          icon="XCircleIcon"
                          size="14"
                          class="mr-50"
                        />
                        Les mots de passe ne correspondent pas
                      </small>
                    </div>
                    <small
                      v-if="errors.length > 0"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <b-row>
              <b-col cols="12">
                <div class="text-center mb-3">
                  <PasswordGenerator
                    :form-data="formData"
                    user-type="deliveryman"
                    @generated="onPasswordGenerated"
                  />
                </div>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-3">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'delivery-man-list' }"
                aria-label="Annuler et revenir à la liste des livreurs"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                Annuler
              </b-button>

              <span id="deliveryman-create-save-btn-wrapper" class="d-inline-block">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="!isFormValid || isSubmitting"
                  :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                  aria-label="Enregistrer le nouveau livreur"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                    aria-hidden="true"
                  />
                  Enregistrer
                  <span class="sr-only">Valider l'ajout du livreur à la flotte logistique</span>
                </b-button>
              </span>
              <b-tooltip
                v-if="!isFormValid || isSubmitting"
                target="deliveryman-create-save-btn-wrapper"
                triggers="hover"
                placement="top"
              >
                {{ tooltipMessage }}
              </b-tooltip>
            </div>
          </b-card>
        </b-form>
      </validation-observer>

      <!-- Quick create car modal -->
      <quick-create-car-modal
        ref="quickCreateCarModal"
        @car-created="onCarCreated"
      />
    </b-overlay>
  </b-card>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, email, numeric, length, min, confirmed,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  BOverlay,
  BFormTextarea,
  BFormCheckbox,
  BProgress,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import '@validations'
import axios from 'axios'
import QuickCreateCarModal from '@/components/modals/QuickCreateCarModal.vue'

extend('required', required)
extend('email', email)
extend('numeric', numeric)
extend('length', length)
extend('min', min)
extend('confirmed', confirmed)

export default {
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BOverlay,
    BFormCheckbox,
    BProgress,
    vSelect,
    QuickCreateCarModal,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      repositories: [],
      cars: [],
      showPassword: false,
      showConfirmPassword: false,
      formData: {
        username: '',
        email: '',
        phone: '',
        address: '',
        repository: null,
        car: null,
        password: '',
        confirm_password: '',
        pay_type: null,
        compensation: null,
        owns_vehicle: false,
        vehicle_monthly_cost: null,
      },
      paymentTypeOptions: [
        { value: 'salary', label: 'Salaire' },
        { value: 'commission', label: 'Commission (Par Colis)' },
      ],
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.formData.username && this.formData.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du livreur'
    },

    repositoryOptions() {
      if (!Array.isArray(this.repositories)) return []

      // Ensure each repository has a name field
      const options = this.repositories.map(repo => {
        if (repo && typeof repo === 'object') {
          return {
            ...repo,
            name: repo.name || repo.repository_name || repo.title || repo.location || repo.address || `Dépôt ${repo.id}` || 'Dépôt inconnu',
          }
        }
        return repo
      })

      return options
    },
    carOptions() {
      if (!Array.isArray(this.cars)) return []

      const options = this.cars.map(car => ({
        ...car,
        displayName: `${car.brand || 'N/A'} - ${car.serial_number || 'N/A'} (${car.color || 'N/A'})`,
      }))

      return options
    },
    compensationLabel() {
      if (this.formData.pay_type === 'salary') {
        return 'Salaire Mensuel (DT)'
      }
      if (this.formData.pay_type === 'commission') {
        return 'Taux par Colis (DT)'
      }
      return 'Montant (DT)'
    },
    compensationDescription() {
      if (this.formData.pay_type === 'salary') {
        return 'Salaire mensuel fixe du livreur.'
      }
      if (this.formData.pay_type === 'commission') {
        return 'Montant payé par colis livré.'
      }
      return 'Montant de la rémunération.'
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.formData

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.formData.password
        && this.formData.confirm_password
        && this.formData.password === this.formData.confirm_password
      )
    },
    isFormValid() {
      return (
        this.formData.username !== ''
        && this.formData.email !== ''
        && this.formData.phone !== ''
        && this.formData.repository !== null
        && this.formData.password !== ''
        && this.formData.password.length >= 8
        && this.passwordsMatch
      )
    },
    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      const missing = []
      if (!this.formData.username || !this.formData.username.trim()) missing.push('Nom')
      if (!this.formData.email || !this.formData.email.trim()) missing.push('Email')
      if (!this.formData.phone || !this.formData.phone.trim()) missing.push('Téléphone')
      if (!this.formData.repository) missing.push('Dépôt')
      if (!this.formData.password || this.formData.password.length < 8) missing.push('Mot de passe (min 8 caractères)')
      if (!this.passwordsMatch) missing.push('Confirmation mot de passe')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([
        this.fetchRepositories(),
        this.fetchCars(),
      ])
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    generateCompanyEmail() {
      if (!this.formData.username || !this.formData.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du livreur.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.formData.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.formData.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    async onCarCreated(newCar) {
      if (!Array.isArray(this.cars)) {
        this.cars = []
      }
      this.cars = [...this.cars, newCar]
      await this.$nextTick()
      this.formData.car = newCar.id
    },
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      try {
        const response = await axios.get('/api/repositories/')

        // Handle the response data
        let repositories = []
        if (Array.isArray(response.data)) {
          repositories = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          // Handle paginated response
          repositories = response.data.results
        } else if (response.data && typeof response.data === 'object') {
          // If it's an object with array properties, try to find the array
          const possibleArrays = Object.values(response.data).filter(Array.isArray)
          repositories = possibleArrays.length > 0 ? possibleArrays[0] : []
        }

        this.repositories = repositories
      } catch (error) {
        this.repositories = []
        this.showToast('danger', 'Erreur lors du chargement des dépôts')
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/')

        // Handle the response data
        let cars = []
        if (Array.isArray(response.data)) {
          cars = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          // Handle paginated response
          cars = response.data.results
        } else if (response.data && typeof response.data === 'object') {
          // If it's an object with array properties, try to find the array
          const possibleArrays = Object.values(response.data).filter(Array.isArray)
          cars = possibleArrays.length > 0 ? possibleArrays[0] : []
        }

        this.cars = cars
      } catch (error) {
        this.cars = []
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    validateForm() {
      // Basic validation if ValidationObserver is not available
      if (!this.formData.username) {
        this.showToast('warning', 'Le nom est requis')
        return false
      }
      if (!this.formData.email) {
        this.showToast('warning', "L'email est requis")
        return false
      }
      if (!this.formData.phone || this.formData.phone.length !== 8) {
        this.showToast('warning', 'Le numéro de téléphone doit contenir 8 chiffres')
        return false
      }
      if (!this.formData.repository) {
        this.showToast('warning', 'Veuillez sélectionner un dépôt')
        return false
      }
      if (!this.formData.password || this.formData.password.length < 8) {
        this.showToast('warning', 'Le mot de passe doit contenir au moins 8 caractères')
        return false
      }
      if (this.formData.password !== this.formData.confirm_password) {
        this.showToast('warning', 'Les mots de passe ne correspondent pas')
        return false
      }
      return true
    },

    async validateAndSubmit() {
      const observer = this.$refs.formValidator
      if (!observer) {
        if (this.validateForm()) {
          await this.submitForm()
        }
        return
      }

      const success = await observer.validate()
      if (success) {
        await this.submitForm()
      }
    },

    async submitForm() {
      const parsePrice = val => {
        if (val === null || val === undefined || val === '') return null
        const normalized = String(val).replace(',', '.')
        const num = parseFloat(normalized)
        return Number.isNaN(num) ? val : num
      }
      this.isSubmitting = true

      try {
        const payload = {
          username: this.formData.username,
          email: this.formData.email,
          phone: this.formData.phone,
          phone2: this.formData.phone2,
          address: this.formData.address,
          repository: this.formData.repository,
          car: this.formData.car,
          password: this.formData.password,
          confirm_password: this.formData.confirm_password,
          pay_type: this.formData.pay_type,
          compensation: parsePrice(this.formData.compensation),
          owns_vehicle: this.formData.owns_vehicle,
          vehicle_monthly_cost: this.formData.vehicle_monthly_cost,
        }

        await axios.post('/api/auth/register/driver/', payload)

        this.showToast('success', 'Livreur ajouté avec succès')
        this.$router.push({ name: 'delivery-man-list' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating delivery man:', error)

        this.showToast('danger', this.extractCreateErrorMessage(error))
      } finally {
        this.isSubmitting = false
      }
    },

    extractCreateErrorMessage(error) {
      const fallback = 'Erreur lors de la création du livreur'
      const errorData = error.response && error.response.data

      if (!errorData) return fallback

      if (typeof errorData === 'string') {
        return this.extractDjangoValidationError(errorData) || errorData
      }

      return this.formatValidationError(errorData) || fallback
    },

    extractDjangoValidationError(html) {
      const text = this.htmlToText(html)
      const match = text.match(/ValidationError at .*?\s*(\{[\s\S]*?\})\s*(?:Request Method|Request URL|Django Version|Exception Type)/)
      if (!match) return ''

      return this.formatValidationErrorText(match[1])
    },

    htmlToText(html) {
      if (typeof window !== 'undefined' && window.DOMParser) {
        const doc = new window.DOMParser().parseFromString(html, 'text/html')
        return doc.body ? doc.body.textContent : html
      }

      return html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&amp;/g, '&')
    },

    formatValidationError(errorData) {
      const directMessage = errorData.message || errorData.detail || errorData.error
      if (directMessage) return this.normalizeErrorValue(directMessage)

      const fieldPriority = ['username', 'email', 'phone', 'phone2']
      const field = fieldPriority.find(key => errorData[key]) || Object.keys(errorData)[0]
      if (!field) return ''

      return this.formatFieldError(field, this.normalizeErrorValue(errorData[field]))
    },

    formatValidationErrorText(text) {
      const fieldMatch = text.match(/['"]([^'"]+)['"]\s*:\s*\[([^\]]+)\]/)
      if (!fieldMatch) return text

      const message = fieldMatch[2].trim().replace(/^["']|["']$/g, '')
      return this.formatFieldError(fieldMatch[1], message)
    },

    normalizeErrorValue(value) {
      if (Array.isArray(value)) return this.normalizeErrorValue(value[0])
      if (value && typeof value === 'object') return this.formatValidationError(value)
      return String(value || '')
    },

    formatFieldError(field, message) {
      if (!message) return ''

      const duplicateMessages = {
        username: "Ce nom d'utilisateur est déjà utilisé.",
        email: 'Cette adresse email est déjà utilisée.',
        phone: 'Ce numéro de téléphone est déjà utilisé.',
        phone2: 'Ce numéro de téléphone est déjà utilisé.',
      }
      const lowerMessage = message.toLowerCase()

      if (
        duplicateMessages[field]
        && (lowerMessage.includes('déjà utilisé') || lowerMessage.includes('existe') || lowerMessage.includes('already'))
      ) {
        return duplicateMessages[field]
      }

      return message
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },

    // Password-related methods
    onPasswordInput() {
      // Trigger real-time validation
      this.$forceUpdate()
    },

    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false

      // Show validation state only if user has typed in confirm field
      if (!this.formData.confirm_password) return null

      // Real-time match validation
      return !!this.passwordsMatch
    },

    onPasswordGenerated(generatedPassword) {
      this.formData.password = generatedPassword
      this.formData.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}

/* Password Strength Indicator Styling */
.password-strength-wrapper {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;

  .strength-label {
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-size: 0.7rem;
  }

  .strength-text {
    font-weight: 700;
    font-size: 0.75rem;

    &.text-danger {
      color: #dc3545 !important;
    }

    &.text-warning {
      color: #ffc107 !important;
    }

    &.text-info {
      color: #17a2b8 !important;
    }

    &.text-success {
      color: #28a745 !important;
    }
  }

  .premium-progress {
    border-radius: 10px;
    background: #e2e8f0;
    overflow: hidden;

    ::v-deep .progress-bar {
      border-radius: 10px;
      transition: width 0.3s ease;
    }
  }
}

/* Generate Password Button Styling */
.generate-password-btn-centered {
  padding: 0.75rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9375rem;
  border: 2px solid #0d4399;
  color: #0d4399;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

  &:hover {
    background: linear-gradient(135deg, #0d4399 0%, #0a3370 100%);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    border-color: #0d4399;
  }

  &:active {
    transform: translateY(0);
  }
}

/* Validation Message Animation */
.validation-message-animate {
  animation: fadeInSlide 0.3s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #0d4399;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
